import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import axios from "axios";
import "../styles/UploadStatus.css";
import { notificationText } from "./notificationText";

const UploadContext = createContext(null);

function formatUploadError(error) {
  const data = error?.response?.data;
  if (typeof data === "string" && data) return data;
  if (data != null) {
    try {
      return JSON.stringify(data);
    } catch (_) {
      /* ignore */
    }
  }
  return error?.message || "Upload failed.";
}

export function UploadProvider({ children }) {
  const [jobs, setJobs] = useState([]);

  const startFileUpload = useCallback(
    ({ url, file, formData, successMessage, onSuccess }) => {
      if (!url || !file) {
        window.alert("Please upload a file.");
        return null;
      }

      const id = `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
      const fileName = file.name || "file";
      const data =
        formData ||
        (() => {
          const fd = new FormData();
          fd.append("file", file);
          return fd;
        })();

      setJobs((prev) => [
        ...prev,
        { id, fileName, progress: 0, status: "uploading" },
      ]);

      let delta = 80;
      let timer = null;

      const patchJob = (patch) => {
        setJobs((prev) =>
          prev.map((job) => (job.id === id ? { ...job, ...patch } : job))
        );
      };

      axios
        .post(url, data, {
          headers: { "Content-Type": "multipart/form-data" },
          timeout: 12 * 60 * 60 * 1000,
          onUploadProgress: (progressEvent) => {
            if (!(progressEvent.total > 0)) return;
            const percent = Math.round(
              (progressEvent.loaded / progressEvent.total) * 100
            );
            const dataTotal = progressEvent.total / (1024 * 1024);
            if (percent <= 80) {
              patchJob({ progress: percent });
              return;
            }
            if (timer) return;
            timer = setInterval(() => {
              delta += (100 - delta) / (0.11 * dataTotal + 13);
              const newProgress = Math.min(99, Math.round(delta));
              patchJob({ progress: newProgress });
              if (delta >= 100) {
                clearInterval(timer);
                timer = null;
                patchJob({ progress: 99 });
              }
            }, 1000);
          },
        })
        .then(() => {
          if (timer) {
            clearInterval(timer);
            timer = null;
          }
          patchJob({ progress: 100, status: "done" });
          window.alert(
            successMessage || `File '${fileName}' upload success.`
          );
          try {
            onSuccess?.();
          } catch (_) {
            /* ignore callback errors */
          }
          setTimeout(() => {
            setJobs((prev) => prev.filter((job) => job.id !== id));
          }, 2000);
        })
        .catch((error) => {
          if (timer) {
            clearInterval(timer);
            timer = null;
          }
          patchJob({ progress: 0, status: "error" });
          window.alert(formatUploadError(error));
          setTimeout(() => {
            setJobs((prev) => prev.filter((job) => job.id !== id));
          }, 3000);
        });

      return id;
    },
    []
  );

  const value = useMemo(
    () => ({ startFileUpload, jobs }),
    [startFileUpload, jobs]
  );

  return (
    <UploadContext.Provider value={value}>
      {children}
      <UploadStatusBar jobs={jobs} />
    </UploadContext.Provider>
  );
}

function UploadStatusBar({ jobs }) {
  if (!jobs.length) return null;
  return (
    <div className="global-upload-status" aria-live="polite">
      {jobs.map((job) => (
        <div key={job.id} className="global-upload-status-item">
          <div className="global-upload-status-row">
            <span className="global-upload-status-name" title={job.fileName}>
              {job.fileName}
            </span>
            <span className="global-upload-status-meta">
              {job.status === "uploading"
                ? `${job.progress}%`
                : job.status === "done"
                  ? notificationText("Done")
                  : notificationText("Failed")}
            </span>
          </div>
          {job.status === "uploading" ? (
            <progress value={job.progress} max="100" />
          ) : null}
        </div>
      ))}
    </div>
  );
}

export function useUploadManager() {
  const ctx = useContext(UploadContext);
  if (!ctx) {
    throw new Error("useUploadManager must be used within UploadProvider");
  }
  return ctx;
}
